import { Link, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { getPaymentById, updatePaymentStatus } from "@/services/paymentService";
import type { Payment, FirestoreDate } from "@/types/payment";
import { PaymentStatus, PaymentGatewayType } from "@/types/payment";
import { queryKeys } from "@/lib/queryKeys";
import {
  ArrowLeft,
  CreditCard,
  DollarSign,
  User,
  Calendar,
  CheckCircle,
  XCircle,
  FileText,
  Receipt,
  Hash,
} from "lucide-react";
import { toast } from "sonner";
import { useState } from "react";

/* ------------------------------------------------------------------ */
/*  Helpers                                                           */
/* ------------------------------------------------------------------ */

function formatFirestoreDate(value: FirestoreDate | null | undefined): string {
  if (value == null) return "--";
  const d =
    value instanceof Date
      ? value
      : "toDate" in value && typeof (value as { toDate: () => Date }).toDate === "function"
        ? (value as { toDate: () => Date }).toDate()
        : "seconds" in value
          ? new Date((value as { seconds: number }).seconds * 1000)
          : null;
  return d
    ? d.toLocaleDateString("es-DO", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : "--";
}

function formatCurrency(amount: number): string {
  return `RD$ ${amount.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const STATUS_LABELS: Record<string, string> = {
  [PaymentStatus.PENDING]: "Pendiente",
  [PaymentStatus.PROCESSING]: "Procesando",
  [PaymentStatus.COMPLETED]: "Completado",
  [PaymentStatus.FAILED]: "Fallido",
  [PaymentStatus.CANCELLED]: "Cancelado",
  [PaymentStatus.REFUNDED]: "Reembolsado",
  [PaymentStatus.MANUAL_VERIFICATION]: "Por Verificar",
};

const STATUS_COLORS: Record<string, string> = {
  [PaymentStatus.PENDING]: "bg-yellow-100 text-yellow-800 border-yellow-300",
  [PaymentStatus.PROCESSING]: "bg-blue-100 text-blue-800 border-blue-300",
  [PaymentStatus.COMPLETED]: "bg-green-100 text-green-800 border-green-300",
  [PaymentStatus.FAILED]: "bg-red-100 text-red-800 border-red-300",
  [PaymentStatus.CANCELLED]: "bg-gray-100 text-gray-800 border-gray-300",
  [PaymentStatus.REFUNDED]: "bg-orange-100 text-orange-800 border-orange-300",
  [PaymentStatus.MANUAL_VERIFICATION]: "bg-purple-100 text-purple-800 border-purple-300",
};

const GATEWAY_LABELS: Record<string, string> = {
  [PaymentGatewayType.CARDNET]: "CARDNET",
  [PaymentGatewayType.AZUL]: "AZUL",
  [PaymentGatewayType.TPAGO]: "TPAGO",
  [PaymentGatewayType.PAYPAL]: "PayPal",
  [PaymentGatewayType.MANUAL_BANK]: "Transferencia Bancaria",
  [PaymentGatewayType.CASH]: "Efectivo",
  [PaymentGatewayType.STRIPE]: "Stripe",
};

function Spec({ label, value }: { label: string; value?: string | number | null }) {
  if (value == null || value === "") return null;
  return (
    <div className="flex justify-between gap-4 py-1.5 text-sm border-b border-border/50 last:border-0">
      <span className="text-muted-foreground shrink-0">{label}</span>
      <span className="font-medium text-right">{String(value)}</span>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main component                                                    */
/* ------------------------------------------------------------------ */

export function PaymentDetail() {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [actionLoading, setActionLoading] = useState(false);

  const { data: rawPayment, isLoading } = useQuery({
    queryKey: queryKeys.payment(id!),
    queryFn: () => getPaymentById(id!),
    enabled: Boolean(id),
  });

  const payment = rawPayment as Payment | null | undefined;

  async function handleVerify() {
    if (!payment) return;
    setActionLoading(true);
    try {
      await updatePaymentStatus(payment.id, PaymentStatus.COMPLETED, "Verificado manualmente");
      toast.success("Pago verificado exitosamente");
      await queryClient.invalidateQueries({ queryKey: queryKeys.payment(payment.id) });
      await queryClient.invalidateQueries({ queryKey: queryKeys.payments });
    } catch {
      toast.error("Error al verificar el pago");
    } finally {
      setActionLoading(false);
    }
  }

  async function handleReject() {
    if (!payment) return;
    setActionLoading(true);
    try {
      await updatePaymentStatus(payment.id, PaymentStatus.FAILED, "Rechazado manualmente");
      toast.success("Pago rechazado");
      await queryClient.invalidateQueries({ queryKey: queryKeys.payment(payment.id) });
      await queryClient.invalidateQueries({ queryKey: queryKeys.payments });
    } catch {
      toast.error("Error al rechazar el pago");
    } finally {
      setActionLoading(false);
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/payments"><ArrowLeft className="h-4 w-4" /></Link>
          </Button>
          <h1 className="font-display text-2xl font-semibold">Pago</h1>
        </div>
        <p className="text-muted-foreground">Cargando...</p>
      </div>
    );
  }

  if (!payment) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/payments"><ArrowLeft className="h-4 w-4" /></Link>
          </Button>
          <h1 className="font-display text-2xl font-semibold">Pago</h1>
        </div>
        <Card>
          <CardContent className="py-12 text-center">
            <CreditCard className="mx-auto h-12 w-12 text-muted-foreground/50" />
            <p className="mt-4 text-muted-foreground">Pago no encontrado</p>
            <Button asChild className="mt-4">
              <Link to="/payments">Volver a pagos</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-4xl space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/payments">
              <ArrowLeft className="h-4 w-4" />
              <span className="sr-only">Volver</span>
            </Link>
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-display text-2xl font-semibold">
                {formatCurrency(payment.amount)}
              </h1>
              <Badge variant="outline" className={STATUS_COLORS[payment.status] ?? ""}>
                {STATUS_LABELS[payment.status] ?? payment.status}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground">
              {payment.userName ?? "Usuario desconocido"} · {formatFirestoreDate(payment.paymentDate ?? payment.createdAt)}
            </p>
          </div>
        </div>
        {payment.status === PaymentStatus.MANUAL_VERIFICATION && (
          <div className="flex gap-2">
            <Button
              variant="destructive"
              onClick={handleReject}
              disabled={actionLoading}
              className="gap-2"
            >
              <XCircle className="h-4 w-4" />
              Rechazar
            </Button>
            <Button
              onClick={handleVerify}
              disabled={actionLoading}
              className="gap-2"
            >
              <CheckCircle className="h-4 w-4" />
              Verificar
            </Button>
          </div>
        )}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Payment info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <DollarSign className="h-4 w-4" />
              Información del pago
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            <Spec label="Monto" value={formatCurrency(payment.amount)} />
            <Spec label="Moneda" value={payment.currency} />
            <Spec label="Método" value={GATEWAY_LABELS[payment.gatewayType] ?? payment.gatewayType} />
            <Spec label="Estado" value={STATUS_LABELS[payment.status] ?? payment.status} />
            {payment.processingFee != null && (
              <Spec label="Comisión" value={formatCurrency(payment.processingFee)} />
            )}
            {payment.netAmount != null && (
              <Spec label="Monto neto" value={formatCurrency(payment.netAmount)} />
            )}
          </CardContent>
        </Card>

        {/* User info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <User className="h-4 w-4" />
              Usuario
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            <Spec label="Nombre" value={payment.userName} />
            <Spec label="Email" value={payment.userEmail} />
            {payment.userId && (
              <div className="pt-2">
                <Button variant="outline" size="sm" asChild>
                  <Link to={`/users/${payment.userId}`}>Ver usuario</Link>
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* References */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Hash className="h-4 w-4" />
              Referencias
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            <Spec label="ID Transacción" value={payment.transactionId} />
            <Spec label="Referencia" value={payment.reference} />
            <Spec label="Orden" value={payment.orderId} />
            {payment.financingRequestId && (
              <>
                <Spec label="Financiamiento" value={payment.financingRequestId} />
                <div className="pt-2">
                  <Button variant="outline" size="sm" asChild>
                    <Link to={`/financings/${payment.financingRequestId}`}>Ver financiamiento</Link>
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Dates */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Calendar className="h-4 w-4" />
              Fechas
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            <Spec label="Fecha de pago" value={formatFirestoreDate(payment.paymentDate)} />
            <Spec label="Creado" value={formatFirestoreDate(payment.createdAt)} />
            <Spec label="Actualizado" value={formatFirestoreDate(payment.updatedAt)} />
            <div className="pt-2 font-mono text-xs text-muted-foreground">ID: {payment.id}</div>
          </CardContent>
        </Card>
      </div>

      {/* Voucher */}
      {payment.voucherUrl && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Receipt className="h-4 w-4" />
              Comprobante
              {payment.voucherVerified && (
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300 ml-2">Verificado</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <img
              src={payment.voucherUrl}
              alt="Comprobante de pago"
              className="max-h-80 rounded-lg border object-contain"
            />
          </CardContent>
        </Card>
      )}

      {/* Notes */}
      {payment.notes && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <FileText className="h-4 w-4" />
              Notas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="whitespace-pre-wrap text-sm">{payment.notes}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
